import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useState, useCallback } from 'react';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ConfirmProvider } from './context/ConfirmContext';
import { ToastProvider, useToast } from './context/ToastContext';
import { ThemeProvider } from './context/ThemeContext';

import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { StatusBar, Style } from '@capacitor/status-bar';
import AttachmentCacheService from './services/attachmentCacheService';
import { user as userApi, recordatorios as recordatoriosApi } from './services/api';
import NativeService from './services/nativeService';

// Layouts
import DashboardLayout from './components/DashboardLayout';
import UserLayout from './components/layouts/UserLayout';
import RequirePermission from './components/RequirePermission';

// Admin Pages
import Calendar from './pages/Calendar';
import Conductores from './pages/Conductores';
import GruposVacaciones from './pages/GruposVacaciones';
import GruposLibranzas from './pages/GruposLibranzas';
import Festivos from './pages/Festivos';
import InvitationCodes from './pages/InvitationCodes';
import Users from './pages/Users';
import Avisos from './pages/Avisos';
import HistorialAvisos from './pages/HistorialAvisos';
import MotivosAdmin from './pages/MotivosAdmin';
import AnexoLibranzas from './pages/AnexoLibranzas';
import TurnosAdmin from './pages/TurnosAdmin';
import LineasAdmin from './pages/LineasAdmin';
import PrimasAdmin from './pages/PrimasAdmin';
import AuditAdmin from './pages/AuditAdmin';
import UserActivityAdmin from './pages/UserActivityAdmin';
import RolesAdmin from './pages/RolesAdmin';
import AccidentesAdmin from './pages/AccidentesAdmin';
import AdminSettings from './pages/AdminSettings';
import ImportNombramientoAdmin from './pages/ImportNombramientoAdmin';
import GmailConfigAdmin from './pages/GmailConfigAdmin';
import EstadisticasAdmin from './pages/EstadisticasAdmin';
import MetricasImpactoAdmin from './pages/MetricasImpactoAdmin';

// User Pages
import UserDashboard from './pages/UserDashboard';
import OpePage from './pages/OpePage';
import Settings from './pages/Settings';
import Login from './pages/Login';
import AsuntosPropios from './pages/AsuntosPropios';
import AsuntosPropiosAdmin from './pages/AsuntosPropiosAdmin';
import HorasSindicalesConfig from './pages/HorasSindicalesConfig';
import CambiosDias from './pages/CambiosDias';
import CambiosHistorialAdmin from './pages/CambiosHistorialAdmin';
import Historial from './pages/Historial';
import Averia from './pages/Averia';
import Accidentes from './pages/Accidentes';
import BolsaDias from './pages/BolsaDias';
import HorasSindicales from './pages/HorasSindicales';

// Legal Pages
import LegalLayout from './components/layouts/LegalLayout';
import PoliticaPrivacidad from './pages/legal/PoliticaPrivacidad';
import PoliticaCookies from './pages/legal/PoliticaCookies';
import AvisoLegal from './pages/legal/AvisoLegal';
import CookieBanner from './components/CookieBanner';

import './index.css';

// Initialize native features
const initNativeFeatures = async () => {
  if (Capacitor.isNativePlatform()) {
    try {
      await StatusBar.hide();
    } catch (error) {
      console.error('Error hiding status bar:', error);
    }

    // Initialize attachment cache database
    try {
      const dbInitialized = await AttachmentCacheService.initDatabase();
      if (dbInitialized) {
        // Run cleanup in background after a short delay (to not block startup)
        setTimeout(async () => {
          try {
            const token = localStorage.getItem('token');
            if (token) {
              // Get active aviso IDs from server
              const response = await userApi.getActiveAvisoIds();
              const activeIds = response?.ids || [];
              // Clean up orphaned cached files
              const result = await AttachmentCacheService.cleanupOrphans(activeIds);
              // Cleanup completed (silent)
            }
          } catch (e) {
            console.warn('Cache cleanup skipped:', e.message);
          }
        }, 5000); // 5 second delay
      }
    } catch (error) {
      console.error('Error initializing attachment cache:', error);
    }
  }
};

// Call on app start
initNativeFeatures();

// Protected route wrapper
const ProtectedRoute = ({ children, requireAdmin = false }) => {
  const { user, loading, isAdmin } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg-primary)' }}>
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-4 border-cyan-400 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gray-400">Cargando...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (requireAdmin && !isAdmin()) {
    return <Navigate to="/user" replace />;
  }

  return children;
};

// Placeholder pages
const PlaceholderPage = ({ title }) => (
  <div className="flex flex-col items-center justify-center min-h-[400px]">
    <h1 className="text-2xl font-bold text-white mb-2">{title}</h1>
    <p className="text-gray-400">Página en desarrollo...</p>
  </div>
);

// Smart redirect based on role
const HomeRedirect = () => {
  const { user, isAdmin } = useAuth();

  if (!user) return <Navigate to="/login" replace />;
  if (isAdmin()) return <Navigate to="/admin" replace />;
  return <Navigate to="/user" replace />;
};

function AppRoutes() {
  const { user } = useAuth();
  const toast = useToast();

  // Initialize Native Push Notifications (APK)
  useEffect(() => {
    if (user && Capacitor.isNativePlatform()) {
      NativeService.initPushNotifications().then(result => {
        // Push notifications initialized (silent)
      });
    } else if (user && 'Notification' in window && Notification.permission === 'default') {
      // Solicitar permisos en Web
      Notification.requestPermission();
    }
  }, [user]);

  // Web Polling for Reminders (Web Only)
  useEffect(() => {
    if (!user || Capacitor.isNativePlatform()) return;

    const checkReminders = async () => {
      try {
        const now = new Date();
        const dateStr = now.toISOString().split('T')[0];
        const timeStr = now.toTimeString().slice(0, 5); // HH:MM

        const reminder = await recordatoriosApi.get(dateStr);

        if (reminder && reminder.hora) {
          const [remH, remM] = reminder.hora.split(':').map(Number);
          const nowH = now.getHours();
          const nowM = now.getMinutes();

          const reminderMinutes = remH * 60 + remM;
          const currentMinutes = nowH * 60 + nowM;

          // Check if time matches OR is in the past (within last 24h)
          // And we haven't shown it yet in this session
          if (currentMinutes >= reminderMinutes) {

            const lastShownKey = `reminder_shown_${reminder.id}`;
            const isShownRaw = sessionStorage.getItem(lastShownKey);

            // Only show if NOT shown in this session AND it's physically today
            // (Using dateStr check to ensure we don't show old stuff if logic changes)
            if (!isShownRaw) {
              // Show Alert!
              // Triggering reminder alert

              // Try Native Web Notification first
              if (!Capacitor.isNativePlatform() && 'Notification' in window && Notification.permission === 'granted') {
                new Notification('⏰ TUVISA Calendar', {
                  body: `Recordatorio: ${reminder.mensaje || 'Tienes una tarea programada'}`,
                  icon: '/icon.png',
                  requireInteraction: true
                });
              } else {
                // Fallback to Toast
                toast.info(
                  <div className="flex flex-col gap-1">
                    <span className="font-bold">⏰ Recordatorio - {reminder.hora.slice(0, 5)}</span>
                    <span>{reminder.mensaje || 'Tienes un recordatorio programada'}</span>
                  </div>
                  , {
                    autoClose: 8000, // Cierra automáticamente tras 8 segundos
                    closeOnClick: true, // Click para cerrar
                    pauseOnHover: true, // Pausa al pasar el ratón
                    draggable: true,
                    position: 'top-center',
                    theme: 'dark'
                  });
              }

              // Play beep
              try {
                const audio = new Audio('https://assets.mixkit.co/sfx/preview/mixkit-software-interface-start-2574.mp3');
                audio.play().catch(() => { });
              } catch (e) {
                console.warn('Audio error', e);
              }

              // Mark as shown
              sessionStorage.setItem(lastShownKey, 'true');
            }
          }
        }
      } catch (error) {
        console.error('Error checking reminders', error);
      }
    };

    // Check immediately and then every 30 seconds
    checkReminders();
    const interval = setInterval(checkReminders, 30000);
    return () => clearInterval(interval);
  }, [user, toast]);

  return (
    <Routes>
      {/* Public */}
      <Route path="/login" element={user ? <HomeRedirect /> : <Login />} />

      {/* Legal Pages (public, no auth required) */}
      <Route element={<LegalLayout />}>
        <Route path="/privacidad" element={<PoliticaPrivacidad />} />
        <Route path="/cookies" element={<PoliticaCookies />} />
        <Route path="/legal" element={<AvisoLegal />} />
      </Route>

      {/* Smart home redirect */}
      <Route path="/" element={<HomeRedirect />} />

      {/* User Routes */}
      <Route
        path="/user"
        element={
          <ProtectedRoute>
            <UserLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<UserDashboard />} />
        <Route path="ap" element={<AsuntosPropios />} />
        <Route path="cambios" element={<CambiosDias />} />
        <Route path="ope" element={
          <RequirePermission permission="ope.ver" fallback={<Navigate to="/user" replace />}>
            <OpePage />
          </RequirePermission>
        } />
        <Route path="bolsa-dias" element={<BolsaDias />} />
        <Route path="horas-sindicales" element={<HorasSindicales />} />
        <Route path="acciones" element={<PlaceholderPage title="➕ Acciones" />} />
        <Route path="historial" element={<Historial />} />
        <Route path="averia" element={<Averia />} />
        <Route path="accidentes" element={<Accidentes />} />
        <Route path="ajustes" element={<Settings />} />
      </Route>

      {/* Admin Routes */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute requireAdmin>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Calendar />} />
        <Route path="conductores" element={<Conductores />} />
        <Route path="grupos-vacaciones" element={<GruposVacaciones />} />
        <Route path="grupos-libranzas" element={<GruposLibranzas />} />
        <Route path="festivos" element={<Festivos />} />
        <Route path="dias-libranza" element={<PlaceholderPage title="Días de Libranza" />} />
        <Route path="lineas-fijas" element={<PlaceholderPage title="Líneas Fijas" />} />
        <Route path="usuarios" element={<Users />} />
        <Route path="invitaciones" element={<InvitationCodes />} />
        <Route path="avisos" element={<Avisos />} />
        <Route path="avisos/historial" element={<HistorialAvisos />} />
        <Route path="motivos" element={<MotivosAdmin />} />
        <Route path="anexo-libranzas" element={<AnexoLibranzas />} />
        <Route path="importar-nombramiento" element={<ImportNombramientoAdmin />} />
        <Route path="gmail-config" element={<GmailConfigAdmin />} />
        <Route path="turnos" element={<TurnosAdmin />} />
        <Route path="lineas" element={<LineasAdmin />} />
        <Route path="primas" element={<PrimasAdmin />} />
        <Route path="auditoria" element={<AuditAdmin />} />
        <Route path="actividad" element={<UserActivityAdmin />} />
        <Route path="asuntos-propios" element={<AsuntosPropiosAdmin />} />
        <Route path="horas-sindicales" element={<HorasSindicalesConfig />} />
        <Route path="cambios-historial" element={<CambiosHistorialAdmin />} />
        <Route path="cambios-historial" element={<CambiosHistorialAdmin />} />
        <Route path="roles" element={<RolesAdmin />} />
        <Route path="accidentes" element={<AccidentesAdmin />} />
        <Route path="ajustes" element={<AdminSettings />} />
        <Route path="estadisticas" element={<EstadisticasAdmin />} />
        <Route path="metricas-impacto" element={<MetricasImpactoAdmin />} />
        <Route path="ope" element={<OpePage />} />
      </Route>

      {/* Catch all */}
      <Route path="*" element={<HomeRedirect />} />
    </Routes>
  );
}

// Global push notification handler - shows toast on any page
function GlobalPushHandler() {
  const toast = useToast();

  useEffect(() => {
    const handlePush = (event) => {
      const notification = event.detail;
      // Push notification received
      // Show toast with notification info
      toast.info(notification?.title || notification?.body || '🔔 Nueva notificación');
    };

    window.addEventListener('pushNotificationReceived', handlePush);
    return () => window.removeEventListener('pushNotificationReceived', handlePush);
  }, [toast]);

  return null;
}

// Android hardware back button handler
function AndroidBackHandler() {
  const navigate = useNavigate();
  const location = useLocation();
  const [showExitDialog, setShowExitDialog] = useState(false);

  // Check if we're at a root path (no more history to go back)
  const isAtRoot = useCallback(() => {
    const rootPaths = ['/', '/user', '/admin', '/login'];
    return rootPaths.includes(location.pathname);
  }, [location.pathname]);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    const handleBackButton = CapacitorApp.addListener('backButton', ({ canGoBack }) => {
      // If exit dialog is showing, close it
      if (showExitDialog) {
        setShowExitDialog(false);
        return;
      }

      // If we can go back in browser history, do that
      if (canGoBack && !isAtRoot()) {
        window.history.back();
      } else {
        // At root level - show exit confirmation
        setShowExitDialog(true);
      }
    });

    return () => {
      handleBackButton.remove();
    };
  }, [isAtRoot, showExitDialog]);

  const handleExitApp = useCallback(() => {
    CapacitorApp.exitApp();
  }, []);

  const handleCancelExit = useCallback(() => {
    setShowExitDialog(false);
  }, []);

  if (!showExitDialog) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.75)' }}
    >
      <div
        className="mx-4 p-6 rounded-2xl shadow-2xl max-w-sm w-full"
        style={{
          background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
          border: '1px solid rgba(56, 189, 248, 0.2)'
        }}
      >
        <div className="text-center">
          <div className="text-4xl mb-4">👋</div>
          <h3 className="text-xl font-bold text-white mb-2">
            ¿Salir de la aplicación?
          </h3>
          <p className="text-gray-400 mb-6">
            ¿Estás seguro de que quieres cerrar TUVISA Calendar?
          </p>
        </div>

        <div className="flex gap-3">
          <button
            onClick={handleCancelExit}
            className="flex-1 py-3 px-4 rounded-xl font-semibold transition-all"
            style={{
              background: 'rgba(100, 116, 139, 0.3)',
              color: '#94a3b8'
            }}
          >
            Cancelar
          </button>
          <button
            onClick={handleExitApp}
            className="flex-1 py-3 px-4 rounded-xl font-semibold transition-all"
            style={{
              background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              color: 'white'
            }}
          >
            Salir
          </button>
        </div>
      </div>
    </div>
  );
}

import { HeaderProvider } from './context/HeaderContext';

function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <AuthProvider>
          <ConfirmProvider>
            <HeaderProvider>
              <ToastProvider>
                <CookieBanner />
                <GlobalPushHandler />
                <AndroidBackHandler />
                <AppRoutes />
              </ToastProvider>
            </HeaderProvider>
          </ConfirmProvider>
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}


export default App;
